import { Injectable, Injector, inject, signal } from '@angular/core';

import { Auth } from 'src/providers/auth/auth';
import { Client } from 'src/providers/client/client';
import { Me } from 'src/providers/me/me';
import { Events } from 'src/providers/events/events';

import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { PmDemoLandingService } from 'src/shared/services/pm-demo-landing.service';
import { PmDemoAccountManagerService } from 'src/shared/services/pm-demo-account-manager.service';
import { PmCreateAccountPhoneService } from 'src/shared/services/pm-create-account-phone.service';
import { PmScheduleCallSelectionService } from 'src/shared/services/pm-schedule-call-selection.service';
import { PmScheduleCallFirstService } from 'src/shared/services/pm-schedule-call-first.service';
import { PmDemoTourService } from 'src/shared/services/pm-demo-tour.service';
import { PosthogService } from 'src/shared/services/posthog.service';
import { GenerateUuid } from 'src/providers/uuid/uuid';
import { DEMO_ACCOUNT_EMAIL, DEMO_ACCOUNT_FLAG_KEY, DEMO_ADDRESSES_KEY } from 'src/shared/services/demo-data.service';

import { CustomRouteReuseStrategy } from 'src/custom-route-reuse-strategy';

export type DemoEntrancePhase = 'idle' | 'cover' | 'reveal' | 'welcome';

// Shown when the demo can't be loaded — almost always because the shared demo account's login
// failed (e.g. its credentials were changed). Distinct from the generic "Something went wrong"
// so a broken demo account is diagnosable from a screenshot rather than looking like any error.
export const DEMO_UNAVAILABLE_MESSAGE = 'The demo is temporarily unavailable. Please try again later or contact support.';

export interface DemoPendingSignup {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface PropertyLiveStatus {
  total: number;
  live: number;
  properties: PropertyLiveInfo[];
}

export interface PropertyLiveInfo {
  id: number;
  name: string;
  city: string;
  isLive: boolean;
}

export interface OnboardingActionItem {
  id: string;
  title: string;
  description: string;
  icon: string;
  // When true, clicking the item reopens the Get Started flow (which resumes on the
  // step the user left off on) rather than navigating to a standalone destination.
  resumesFlow?: boolean;
  // The property this item belongs to, so it can be listed under that property.
  addressId?: number;
}

export type ActionItemId = 'add_property' | 'schedule_call' | 'connect_listings';

export interface ActionItemStatus {
  id: ActionItemId;
  title: string;
  description: string;
  done: boolean;
  // The Get Started flow step that handles this item (so clicking it opens there).
  step: number;
}

export interface PropertyOnboarding {
  addressId: number | null; // null = the not-yet-added first-property placeholder
  name: string;
  city: string;
  isLive: boolean;
  items: ActionItemStatus[];
}

export interface OnboardingState {
  // Account-level: the goal picked on the Go Live page and whether the setup call is
  // booked (both stored as metadata so they survive reloads / new sessions).
  goal: 'projection' | 'learn' | null;
  callScheduled: boolean;
  properties: PropertyOnboarding[];
}

// A single concierge-assigned onboarding step surfaced to the client for one address.
export interface AssignedActionItem {
  stepId: string;
  title: string;
  icon: string;
  done: boolean;
}

// An address with the (possibly empty) set of concierge-assigned onboarding items for it.
export interface AddressAssignedItems {
  addressId: number;
  name: string;
  city: string;
  isLive: boolean;
  items: AssignedActionItem[];
}

/**
 * Drives the property manager demo experience: rental signups are logged into a
 * shared demo account (demos@tidy.com) instead of creating an account. The user's
 * signup details are stashed so their real account can be created later, when they
 * take a real action (e.g. booking a job). Also owns the entrance animation state
 * shown when transitioning from the auth modal into the demo, and marks the
 * session as a demo account when the demo email logs in — capturing the account's
 * first 10 addresses so DemoDataService can map demo responses onto real
 * address ids.
 */
@Injectable({
  providedIn: 'root'
})
export class DemoAccountService {
  // "Schedule" actions book the "Learn More About TIDY" call.
  static readonly SCHEDULE_CALL_URL = 'https://calendly.com/d/dtnf-pfg-9gz/learn-about-tidy';
  private static readonly DEMO_PASSWORD = 'DemoTidy@123';
  private static readonly PENDING_SIGNUP_KEY = 'demoPendingSignup';
  private static readonly WELCOME_PENDING_KEY = 'demoWelcomePending';

  readonly entrancePhase = signal<DemoEntrancePhase>('idle');

  // Mobile bottom-nav onboarding nudge — the slim bar attached above the bottom nav. Mirrors
  // the desktop live pill's single call-out so the two never diverge: 'schedule-call' before
  // the setup call is booked, otherwise 'action-item' when one outstanding item remains, or
  // null (hidden). Populated by app.component.refreshLivePill(); read by the bottom nav.
  readonly onboardingNudgeCallout = signal<'schedule-call' | 'action-item' | null>(null);
  readonly onboardingNudgeLabel = signal<string | null>(null);
  // Whether the account has at least one property. The bar is hidden on the properties page
  // (only) while this is false, to avoid overlapping the "Add a Property" card there.
  readonly onboardingNudgeHasProperty = signal<boolean>(false);

  private client = inject(Client);
  private events = inject(Events);
  private storage = inject(TidyStorage);
  private pmDemoLanding = inject(PmDemoLandingService);
  private pmAccountManager = inject(PmDemoAccountManagerService);
  private pmCreateAccountPhone = inject(PmCreateAccountPhoneService);
  private pmScheduleCallSelection = inject(PmScheduleCallSelectionService);
  private pmScheduleCallFirst = inject(PmScheduleCallFirstService);
  private pmDemoTour = inject(PmDemoTourService);
  private posthogService = inject(PosthogService);
  private injector = inject(Injector);
  private generateUuid = inject(GenerateUuid);
  private entranceTimers: any[] = [];
  private coverStartedAt = 0;
  // Set when a demo→real conversion happens behind the open Get Started dialog. The app
  // reloads the page when that dialog closes so the displayed page (and any cached routes)
  // flush their demo data and re-fetch the new account's real data.
  private pendingRealDataReload = false;

  // Me injects this service (checkAndSetupDemoAccount on load), and Auth injects
  // Me, so both must be resolved lazily to avoid a circular DI error
  private get auth(): Auth {
    return this.injector.get(Auth);
  }

  private get me(): Me {
    return this.injector.get(Me);
  }

  isDemoSession(): boolean {
    try {
      return localStorage?.getItem(DEMO_ACCOUNT_FLAG_KEY) === 'true';
    } catch {
      return false;
    }
  }

  async checkAndSetupDemoAccount(email: string | undefined): Promise<void> {
    if (email?.toLowerCase() !== DEMO_ACCOUNT_EMAIL) {
      localStorage.removeItem(DEMO_ACCOUNT_FLAG_KEY);
      localStorage.removeItem(DEMO_ADDRESSES_KEY);
      return;
    }
    try {
      const addresses = await this.client.getAddresses();
      const demoAddresses = (addresses ?? [])
        .slice(0, 10)
        .map((address) => ({
          id: address.id,
          timezone: address.timezone,
          address_name: address.address_name || address.address1,
          address1: address.address1,
          city: address.city,
          state: address.add_state || address.state,
          zip: address.zip,
        }));
      localStorage.setItem(DEMO_ADDRESSES_KEY, JSON.stringify(demoAddresses));
      localStorage.setItem(DEMO_ACCOUNT_FLAG_KEY, 'true');
    } catch (_e) {
      // Demo setup must never block login; without the flag the app falls back to real data.
    }
  }

  /**
   * Whether the post-conversion onboarding pill applies: an authenticated rental
   * account that isn't the shared demo session.
   */
  isConvertedRentalAccount(): boolean {
    if (!this.auth.isAuthenticated() || this.isDemoSession()) {
      return false;
    }
    return localStorage.getItem('isRentalClient') === 'true' || localStorage.getItem('airbnb') === 'true';
  }

  /**
   * Per-property live status, derived from the `external.onboarding_step.{addressId}.go_live`
   * metadata the Getting Started flow writes when a property goes live — the same
   * source the Getting Started page and desktop nav use.
   */
  async getPropertyLiveStatus(): Promise<PropertyLiveStatus> {
    // Always hit the backend: the cached list goes stale the moment the Get
    // Started flow adds a property (it caches an empty list right after the
    // account conversion, which would hide the pill).
    const addresses = await this.client.getAddresses(true);
    if (!addresses?.length) {
      return { total: 0, live: 0, properties: [] };
    }
    const keys = addresses.map((a: any) => `external.onboarding_step.${a.id}.go_live`);
    let metadata: any = [];
    try {
      metadata = await this.client.getMetadata(keys);
    } catch {
      metadata = [];
    }
    const liveIds = new Set(
      (Array.isArray(metadata) ? metadata : [])
        .filter((m: any) => m?.value === 'completed')
        .map((m: any) => Number((String(m?.key || '').match(/onboarding_step\.(\d+)\.go_live/) || [])[1]))
    );
    const properties: PropertyLiveInfo[] = addresses.map((a: any) => ({
      id: a.id,
      name: a.address_name || a.address1,
      city: [a.city, a.add_state || a.state].filter(Boolean).join(', '),
      isLive: liveIds.has(a.id),
    }));
    return {
      total: properties.length,
      live: properties.filter((p) => p.isLive).length,
      properties,
    };
  }

  // The built-in onboarding steps a concierge operator can assign to the client (Funnel V2).
  // Order here is the display order in the assigned-items surface.
  static readonly ASSIGNABLE_STEP_IDS = [
    'listings', 'subscribe', 'pros', 'notify', 'guidebook', 'legal',
    'checklists', 'locks', 'listing-opt', 'bank', 'pricing', 'restocking',
    'property-photos',
  ] as const;

  // Display metadata (title + icon) per assignable stepId. Icons are confirmed present in
  // src/assets/svg/.
  static readonly STEP_META: Record<string, { title: string; icon: string }> = {
    'listings':    { title: 'Tell Us About Your Listings', icon: 'assets/svg/cube-outline.svg' },
    'subscribe':   { title: 'Subscribe',            icon: 'assets/svg/card-outline.svg' },
    'pros':        { title: 'Add Pros',             icon: 'assets/svg/people-outline.svg' },
    'notify':      { title: 'Notify Pros',          icon: 'assets/svg/megaphone-outline.svg' },
    'guidebook':   { title: 'Share Your Guidebook',    icon: 'assets/svg/book-outline.svg' },
    'legal':       { title: 'Legal Compliance',     icon: 'assets/svg/shield-checkmark-outline.svg' },
    'checklists':  { title: 'Checklists',           icon: 'assets/svg/checkbox-outline.svg' },
    'locks':       { title: 'Connect Locks',        icon: 'assets/svg/lock-closed-outline.svg' },
    'listing-opt': { title: 'Listing Optimization', icon: 'assets/svg/analytics-outline.svg' },
    'bank':        { title: 'Connect Bank',         icon: 'assets/svg/cash-outline.svg' },
    'pricing':     { title: 'Pricing Optimization', icon: 'assets/svg/trending-up-outline.svg' },
    'restocking':  { title: 'Restocking',           icon: 'assets/svg/home-outline.svg' },
    'property-photos': { title: 'Add Property Photos', icon: 'assets/svg/camera-outline.svg' },
  };

  // Account-level "Subscribe" completion (the subscription is account-wide). Written by the
  // subscribe page alongside the per-address rows; read so a client who subscribed before
  // adding a property — or who adds one afterwards — never sees the item again. The
  // non-numeric segment keeps it out of the concierge board's per-address matcher.
  static readonly SUBSCRIBE_ACCOUNT_META_KEY = 'onboarding_step.account.subscribe';

  // stepId used for the account-level "personalized projection" Connect Listing item.
  // It is NOT one of ASSIGNABLE_STEP_IDS (the concierge can't assign it); it's synthesized
  // from the account-level goal + setup-call metadata. Reuses STEP_META['listings'] for title/icon.
  static readonly PROJECTION_LISTING_STEP_ID = 'projection-listings';

  // Reads from the TIDY API return keys WITH the 'external.' prefix; strip it so callers can
  // match against the un-prefixed `onboarding_step.{addressId}.{stepId}` form.
  private stripExternalPrefix(key: string): string {
    return key.startsWith('external.') ? key.slice('external.'.length) : key;
  }

  /**
   * The concierge-assigned onboarding items for each address. For every address × stepId the
   * concierge can assign, we request `external.onboarding_step.{addressId}.{stepId}` and keep
   * ONLY rows whose value is 'assigned' (todo) or 'completed' (done) — i.e. items a concierge
   * operator actually assigned to the client. Anything else (absent rows, the concierge-only
   * `concierge_funnel.*` namespace, which is never requested) stays invisible. getMetadata
   * splits the keys into requests short enough for the API's URL limit.
   */
  async getAssignedActionItemsByAddress(): Promise<AddressAssignedItems[]> {
    const addresses = await this.client.getAddresses(true);
    if (!addresses?.length) { return []; }
    const stepIds = DemoAccountService.ASSIGNABLE_STEP_IDS;
    const keys: string[] = [`external.${DemoAccountService.SUBSCRIBE_ACCOUNT_META_KEY}`];
    for (const a of addresses) {
      keys.push(`external.onboarding_step.${a.id}.go_live`);
      for (const s of stepIds) { keys.push(`external.onboarding_step.${a.id}.${s}`); }
    }
    let metadata: any[] = [];
    try {
      metadata = await this.client.getMetadata(keys);
    } catch {
      metadata = [];
    }
    const metaMap = new Map<string, string>();
    for (const m of (Array.isArray(metadata) ? metadata : [])) {
      if (m?.key !== null && m?.key !== undefined) { metaMap.set(this.stripExternalPrefix(String(m.key)), m.value); }
    }
    const accountSubscribed = metaMap.get(DemoAccountService.SUBSCRIBE_ACCOUNT_META_KEY) === 'completed';
    return addresses.map((a: any): AddressAssignedItems => {
      const items: AssignedActionItem[] = [];
      for (const stepId of stepIds) {
        // An account-wide subscription counts as done for every address, including ones
        // added after the fact (their per-address row was never written).
        const val = metaMap.get(`onboarding_step.${a.id}.${stepId}`)
          ?? (stepId === 'subscribe' && accountSubscribed ? 'completed' : undefined);
        if (val === 'assigned' || val === 'completed') {
          const meta = DemoAccountService.STEP_META[stepId];
          items.push({ stepId, title: meta.title, icon: meta.icon, done: val === 'completed' });
        }
      }
      return {
        addressId: a.id,
        name: a.address_name || a.address1,
        city: [a.city, a.add_state || a.state].filter(Boolean).join(', '),
        isLive: metaMap.get(`onboarding_step.${a.id}.go_live`) === 'completed',
        items,
      };
    });
  }

  // Account-level metadata keys (no address segment) for the chosen goal and the booked
  // setup call, so they survive reloads and the demo→real conversion's localStorage wipe.
  private static readonly GOAL_META_KEY = 'onboarding.goal';
  private static readonly CALL_SCHEDULED_META_KEY = 'onboarding.setup_call_scheduled';
  // Set by concierge (Funnel V2 "Setup Call" checkbox) once the call has happened. When
  // completed, the "call scheduled" UI is replaced by the assigned action items.
  private static readonly CALL_COMPLETED_META_KEY = 'onboarding.setup_call_completed';
  // Marks an account as having originated from the PM demo. This is the membership
  // predicate AND the "Created Account" stage signal for the concierge Funnel V2 board.
  // Written WITHOUT the 'external.' prefix — the TIDY API prepends it.
  private static readonly CREATED_VIA_META_KEY = 'onboarding.created_via';
  private static readonly CREATED_VIA_PM_DEMO = 'pm_demo';

  // Concierge-set target go-live date (Funnel V2 client detail modal). Account-level ISO
  // date (YYYY-MM-DD); surfaced on the account-manager pill and the Get Started page.
  private static readonly TARGET_GO_LIVE_DATE_META_KEY = 'onboarding.target_go_live_date';

  // CLIENT-ONLY localStorage flag for the pm-schedule-call-first experiment: set once the user
  // books a call during the demo session (BEFORE creating an account). It is deliberately NOT
  // account metadata — the demo session is the shared demos@tidy.com account, so we must not
  // write "call scheduled" there. It is wiped on the next demo login / conversion (not in the
  // clearSessionStoragePreservingPosthog preserve-list), so each fresh demo starts clean.
  private static readonly SCF_SCHEDULED_KEY = 'pm_scf_scheduled';

  setOnboardingGoal(goal: 'projection' | 'learn'): Promise<any> {
    // NEVER write to the shared demo account. In schedule-call-first the Schedule Call step is
    // reached BEFORE account creation (still the demo session), so persisting the goal here would
    // stamp onboarding.goal onto demos@tidy.com. The in-flow `this.goal` (which picks the Calendly
    // link) is set separately, so skipping the write changes nothing the user sees; control reaches
    // this only post-conversion (real account), so it still persists there.
    if (this.isDemoSession()) {
      return Promise.resolve(undefined);
    }
    return this.client.addMetadata({ key: DemoAccountService.GOAL_META_KEY, value: goal }).catch(() => undefined);
  }

  markSetupCallScheduled(): Promise<any> {
    // Once a call is scheduled the new-rental booking gate (DemoBlockService.shouldGateNewRental)
    // must stop firing. Clear synchronously so it sticks even if the metadata write below fails.
    try { localStorage.removeItem('justCreatedAccount'); } catch { /* best-effort */ }
    return this.client.addMetadata({ key: DemoAccountService.CALL_SCHEDULED_META_KEY, value: 'true' }).catch(() => undefined);
  }

  // --- pm-schedule-call-first: client-only "scheduled during the demo session" flag ---
  // (See SCF_SCHEDULED_KEY.) Tracks that the user booked a call before creating an account,
  // so the demo indicator can flip "Schedule Call" → "Create Account" and the Get Started flow
  // can mark the REAL account (not the demo account) as scheduled once it's created.
  hasScheduledInDemo(): boolean {
    try { return localStorage.getItem(DemoAccountService.SCF_SCHEDULED_KEY) === 'true'; } catch { return false; }
  }

  markScheduledInDemo(): void {
    try { localStorage.setItem(DemoAccountService.SCF_SCHEDULED_KEY, 'true'); } catch { /* best-effort */ }
  }

  clearScheduledInDemo(): void {
    try { localStorage.removeItem(DemoAccountService.SCF_SCHEDULED_KEY); } catch { /* best-effort */ }
  }

  /**
   * Single source of truth for the demo-session indicator's CTA (used by the desktop pill and
   * the mobile bottom bar so they never diverge). `control` (and every non-schedule-first
   * variant) keeps the legacy "Get Started" → create-account-first (step 1). For
   * `schedule-call-first`: "Schedule Call" → schedule entry (step 2) until the user books a
   * call, then "Create Account" → create-account entry (step 1). getVariant() is a synchronous
   * sticky-cache read, warmed by recordExposure() at demo login, so this is flicker-free.
   */
  demoIndicatorCta(): { label: string; startStep: 1 | 2; subtext: string } {
    if (this.pmScheduleCallFirst.getVariant() !== 'schedule-call-first') {
      // The CTA here is to create an account, so the button says so explicitly.
      return { label: 'Create Account', startStep: 1, subtext: 'Ready to get started?' };
    }
    // schedule-call-first: once they've booked a call, the next step is creating an account, so
    // the indicator confirms the booking (with a call icon, see the desktop pill) instead of the
    // generic "ready to get started?" nudge.
    return this.hasScheduledInDemo()
      ? { label: 'Create Account', startStep: 1, subtext: 'Call scheduled' }
      : { label: 'Schedule Call', startStep: 2, subtext: 'Ready to get started?' };
  }

  /** True when the visitor is in the pm-schedule-call-first variant (schedule before account). */
  isScheduleCallFirstVariant(): boolean {
    return this.pmScheduleCallFirst.getVariant() === 'schedule-call-first';
  }

  /**
   * Copy for the centralized "you tried a blocked action" demo prompt (DemoBlockModal, shown by
   * DemoBlockService from the dozens of demo-gated call sites). Adapts to pm-schedule-call-first:
   * before the visitor has scheduled a call, the prompt nudges them to schedule one (and the
   * DemoBlockService routes the button to the schedule flow); otherwise it keeps the legacy
   * "create an account" nudge. One place to change the copy for every gated action.
   */
  demoBlockPrompt(): { title: string; body: string; buttonLabel: string } {
    if (this.isScheduleCallFirstVariant() && !this.hasScheduledInDemo()) {
      return {
        title: 'Schedule a Call to Get Started',
        body: 'You\'re exploring a demo account. Schedule a quick call with your account manager to get started.',
        buttonLabel: 'Schedule a Call',
      };
    }
    return {
      title: 'Create Account to Get Started',
      body: 'You\'re exploring a demo account. Create your own free account to manage your properties and make changes.',
      buttonLabel: 'Get Started',
    };
  }

  // Whether the account-wide "Subscribe" step is done (one metadata key). Drives the
  // Subscribe item on the no-property placeholder and its share of the pill count.
  async isSubscribeStepCompleted(): Promise<boolean> {
    const key = `external.${DemoAccountService.SUBSCRIBE_ACCOUNT_META_KEY}`;
    try {
      const metadata = await this.client.getMetadata([key]);
      const row = (Array.isArray(metadata) ? metadata : []).find((m: any) => m?.key === key);
      return row?.value === 'completed';
    } catch {
      return false;
    }
  }

  // Lightweight read of just the account-level setup-call-scheduled flag (one metadata key),
  // for surfaces that only need the "call scheduled" indicator without the full onboarding
  // state. Returns false on any error.
  async isSetupCallScheduled(): Promise<boolean> {
    const key = `external.${DemoAccountService.CALL_SCHEDULED_META_KEY}`;
    try {
      const metadata = await this.client.getMetadata([key]);
      const row = (Array.isArray(metadata) ? metadata : []).find((m: any) => m?.key === key);
      return row?.value === 'true';
    } catch {
      return false;
    }
  }

  // Whether the concierge has marked the setup call complete. Once true, the "call
  // scheduled" indicators should be hidden — the call already happened.
  async isSetupCallCompleted(): Promise<boolean> {
    const key = `external.${DemoAccountService.CALL_COMPLETED_META_KEY}`;
    try {
      const metadata = await this.client.getMetadata([key]);
      const row = (Array.isArray(metadata) ? metadata : []).find((m: any) => m?.key === key);
      return row?.value === 'completed';
    } catch {
      return false;
    }
  }

  // The concierge-set target go-live date (account-level metadata), or null when unset.
  // Returns an ISO date string (YYYY-MM-DD). Returns null on any error.
  async getTargetGoLiveDate(): Promise<string | null> {
    const key = `external.${DemoAccountService.TARGET_GO_LIVE_DATE_META_KEY}`;
    try {
      const metadata = await this.client.getMetadata([key]);
      const row = (Array.isArray(metadata) ? metadata : []).find((m: any) => m?.key === key);
      return row?.value ? String(row.value) : null;
    } catch {
      return null;
    }
  }

  // Format an ISO YYYY-MM-DD go-live date for display (e.g. "Aug 15, 2026"). Parsed as a
  // local calendar date to avoid the UTC-parse off-by-one. Returns null for empty/invalid.
  formatGoLiveDate(iso: string | null | undefined): string | null {
    if (!iso) { return null; }
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!match) { return null; }
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[Number(match[2]) - 1]} ${Number(match[3])}, ${match[1]}`;
  }

  // The single action item the mobile bar surfaces once the setup call is booked: the first
  // outstanding concierge item across all addresses, else the "Tell Us About Your Listings"
  // nudge (one standalone when no property exists, or any address still missing the step).
  // Returns null when nothing is outstanding — kept in lockstep with the desktop pill's
  // action-item count.
  firstOutstandingActionItem(assigned: AddressAssignedItems[]): string | null {
    for (const address of assigned) {
      const undone = address.items.find((i) => !i.done);
      if (undone) { return undone.title; }
    }
    const listingsTitle = DemoAccountService.STEP_META['listings'].title;
    if (assigned.length === 0) { return listingsTitle; }
    if (assigned.some((a) => !a.items.some((i) => i.stepId === 'listings'))) { return listingsTitle; }
    return null;
  }

  // Set the mobile bottom-nav nudge from the same data the live pill uses. Before the setup
  // call is booked the nudge prompts "Schedule Setup Call"; afterward it surfaces one
  // outstanding action item, or hides entirely when none remain.
  setOnboardingNudge(callScheduled: boolean, callCompleted: boolean, assigned: AddressAssignedItems[]): void {
    // Whether the user has added a property yet. The bar shows on every page, EXCEPT the
    // properties page while this is false — there the full-bleed "Add a Property" card (with
    // social proof) would overlap it. app.component re-runs refreshLivePill on 'add:address',
    // so this flips true the moment the user adds their first property.
    this.onboardingNudgeHasProperty.set(assigned.length > 0);
    if (!callScheduled && !callCompleted) {
      this.onboardingNudgeCallout.set('schedule-call');
      this.onboardingNudgeLabel.set('Schedule Setup Call');
      return;
    }
    const item = this.firstOutstandingActionItem(assigned);
    this.onboardingNudgeCallout.set(item ? 'action-item' : null);
    this.onboardingNudgeLabel.set(item);
  }

  clearOnboardingNudge(): void {
    this.onboardingNudgeCallout.set(null);
    this.onboardingNudgeLabel.set(null);
    this.onboardingNudgeHasProperty.set(false);
  }

  // Lightweight read of just the account-level onboarding goal (one metadata key).
  // Returns null on absence or any error.
  async getOnboardingGoal(): Promise<'projection' | 'learn' | null> {
    const key = `external.${DemoAccountService.GOAL_META_KEY}`;
    try {
      const metadata = await this.client.getMetadata([key]);
      const row = (Array.isArray(metadata) ? metadata : []).find((m: any) => m?.key === key);
      return row?.value === 'projection' || row?.value === 'learn' ? row.value : null;
    } catch {
      return null;
    }
  }

  /**
   * Whether the single account-level "Connect Listing" projection item should be surfaced
   * on the onboarding-status page and counted in the floating pill. Shared by both surfaces
   * so the rule can't drift between them:
   *  - goal must be 'projection' AND the setup call must be booked, and
   *  - no concierge-assigned 'listings' item exists on any address (otherwise that per-address
   *    listings item already covers it — don't double up).
   */
  static shouldShowProjectionListing(
    goal: 'projection' | 'learn' | null,
    callScheduled: boolean,
    assigned: AddressAssignedItems[],
  ): boolean {
    if (goal !== 'projection' || !callScheduled) { return false; }
    const hasConciergeListings = assigned
      .flatMap((a) => a.items)
      .some((i) => i.stepId === 'listings');
    return !hasConciergeListings;
  }

  /**
   * Onboarding state for the post-conversion pill / status page: the account-level goal
   * and scheduled-call flags (metadata), plus each property with its two action items
   * (Add First Property, Tell Us About Your Listings). Action items only apply to the "personalized
   * projection" goal — the caller decides whether to show them.
   */
  async getOnboardingState(): Promise<OnboardingState> {
    const addresses = await this.client.getAddresses(true);
    const keys: string[] = [
      `external.${DemoAccountService.GOAL_META_KEY}`,
      `external.${DemoAccountService.CALL_SCHEDULED_META_KEY}`,
    ];
    for (const a of (addresses || [])) {
      keys.push(`external.onboarding_step.${a.id}.go_live`);
      keys.push(`external.onboarding_step.${a.id}.listings`);
    }
    let metadata: any[] = [];
    try {
      metadata = await this.client.getMetadata(keys);
    } catch {
      metadata = [];
    }
    const metaMap = new Map<string, string>();
    for (const m of (Array.isArray(metadata) ? metadata : [])) {
      if (m?.key !== null && m?.key !== undefined) { metaMap.set(String(m.key), m.value); }
    }
    const goalRaw = metaMap.get(`external.${DemoAccountService.GOAL_META_KEY}`);
    const goal = goalRaw === 'projection' || goalRaw === 'learn' ? goalRaw : null;
    const callScheduled = metaMap.get(`external.${DemoAccountService.CALL_SCHEDULED_META_KEY}`) === 'true';
    const properties: PropertyOnboarding[] = (addresses || []).map((a: any) => ({
      addressId: a.id,
      name: a.address_name || a.address1,
      city: [a.city, a.add_state || a.state].filter(Boolean).join(', '),
      isLive: this.metaVal(metaMap, a.id, 'go_live') === 'completed',
      // The address exists, so "Add First Property" is done — and dropped entirely.
      items: this.buildActionItems(true, this.metaVal(metaMap, a.id, 'listings') === 'completed'),
    }));
    // Regardless of goal, surface the starting action items on a "Your First Property"
    // placeholder before any address exists (Add First Property + Tell Us About Your Listings).
    if (properties.length === 0) {
      properties.push({
        addressId: null,
        name: 'Your First Property',
        city: '',
        isLive: false,
        items: this.buildActionItems(false, false),
      });
    }
    return { goal, callScheduled, properties };
  }

  private metaVal(map: Map<string, string>, id: number, key: string): string | undefined {
    return map.get(`external.onboarding_step.${id}.${key}`) ?? map.get(`onboarding_step.${id}.${key}`);
  }

  // Action items for a property. "Add First Property" only appears until it's done, then
  // it's dropped entirely. "Tell Us About Your Listings" always appears and persists even once done.
  private buildActionItems(propertyAdded: boolean, connectListings: boolean): ActionItemStatus[] {
    const items: ActionItemStatus[] = [];
    if (!propertyAdded) {
      items.push({ id: 'add_property', title: 'Add First Property', description: 'Add the property you want TIDY to manage.', done: false, step: 4 });
    }
    items.push({ id: 'connect_listings', title: 'Tell Us About Your Listings', description: 'Tell us which Airbnb, VRBO, or Booking.com listings you already have.', done: connectListings, step: 5 });
    return items;
  }

  /**
   * Outstanding onboarding steps. While the user is mid-way through the Get Started
   * flow (its progress is persisted in localStorage and mirrors the
   * `onboarding_step.{addressId}.*` metadata it writes), the single outstanding item
   * is to finish it — e.g. scheduling their setup call — and clicking it resumes the
   * flow on the saved step. Once the flow is complete we fall back to the starter
   * onboarding checklist.
   */
  getOnboardingActionItems(): OnboardingActionItem[] {
    const flowState = this.readGetStartedFlowState();
    if (flowState) {
      // The Calendly step (5) advances to step 6 only after the call is scheduled.
      const callScheduled = (flowState.step ?? 0) >= 6;
      return [{
        id: 'finish_get_started',
        title: callScheduled ? 'Finish Getting Started' : 'Schedule Your Setup Call',
        description: callScheduled
          ? 'Pick up where you left off and finish setting up your property.'
          : 'Pick a time with your account manager to finish getting your property live.',
        icon: 'event',
        resumesFlow: true,
        addressId: flowState.addressId,
      }];
    }
    return [
      {
        id: 'upload_checklists',
        title: 'Upload Checklists',
        description: 'Add cleaning checklists so pros know exactly what to do at each property.',
        icon: 'checklist',
      },
      {
        id: 'add_pros',
        title: 'Add Your Pros',
        description: 'Invite the cleaners and maintenance pros you already work with.',
        icon: 'person_add',
      },
      {
        id: 'connect_listing',
        title: 'Connect Your Listing',
        description: 'Sync bookings automatically from Airbnb, VRBO, or Booking.com.',
        icon: 'link',
      },
    ];
  }

  // The Get Started flow persists its position here (step, addressId, …) from account
  // creation until completion, so its presence means the user has unfinished setup.
  private readGetStartedFlowState(): { step?: number; addressId?: number } | null {
    try {
      const raw = localStorage.getItem('getStartedFlowState');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  async loginToDemoAccount(guestViewType = 'rental'): Promise<void> {
    // Guarantee the demo starts anonymous in PostHog. If the browser still carries a prior
    // identified real-account identity (a previous demo→real conversion, or a real session
    // whose token expired without an explicit logout/reset), the experiment exposures fired
    // below would attach to that stale person — and the later demo→real identify() can't
    // merge identified→identified, so exposure and conversion split across two people and the
    // conversion never attributes. Reset before any exposure; a truly anonymous visitor is
    // left untouched (keeps sticky bucketing). Must run before the recordExposure() calls
    // below AND before PmDemoLandingService.resolveLandingRoute() (every entry point invokes
    // this first). Best-effort — never block the demo flow.
    await this.posthogService.resetIfIdentified().catch(() => { /* never block the demo flow */ });
    // Capture a stable anonymous device id up front. The login flow below wipes
    // localStorage (TidyStorage.clear()), so we re-set this value right before the
    // ping — keeping it non-null and persisting it across sessions so concierge can
    // dedupe repeat demo logins from the same device.
    this.generateUuid.createNewHash();
    const deviceUuid = localStorage.getItem('uuid');
    await this.auth.login(
      { username: DEMO_ACCOUNT_EMAIL, password: DemoAccountService.DEMO_PASSWORD },
      'login',
      null
    );
    const token = localStorage.getItem('tidy_token');
    const tokenType = localStorage.getItem('tidy_token_type');
    await this.clearSessionStoragePreservingPosthog();
    localStorage.setItem('tidy_token', token);
    localStorage.setItem('tidy_token_type', tokenType);
    localStorage.setItem(DEMO_ACCOUNT_FLAG_KEY, 'true');
    localStorage.setItem('guest_view_type', guestViewType);
    localStorage.setItem(DemoAccountService.WELCOME_PENDING_KEY, 'true');
    await this.storage.save('didJustSignIn', true);
    const account = await this.me.load(true);
    this.auth.setAccountTypeStorage(account?.customer_account?.account_type);
    // Booking (and other gated actions) still prompt to create a real account
    await this.storage.save('isGuestAccount', true);
    this.auth.authenticated.set(true);
    this.events.publish('auth:complete');
    this.events.publish('updateMenu');
    // PM-demo experiments all enroll here, on demo login — the same entry point as the
    // landing experiment. Fire-and-forget so they never block the demo flow.
    this.pmAccountManager.recordExposure().catch(() => { /* never block the demo flow */ });
    this.pmCreateAccountPhone.recordExposure().catch(() => { /* never block the demo flow */ });
    this.pmScheduleCallSelection.recordExposure().catch(() => { /* never block the demo flow */ });
    this.pmScheduleCallFirst.recordExposure().catch(() => { /* never block the demo flow */ });
    // Desktop-only — a no-op (no exposure, no bucketing) on mobile.
    this.pmDemoTour.recordExposure().catch(() => { /* never block the demo flow */ });
    this.logPmDemoVariants();
    // Restore the device id (the clears above wipe localStorage) so it persists for
    // next time, then fire-and-forget an anonymous "viewed demo account" event on the
    // shared demo account. Each view is its own metadata row (unique key), keyed by
    // this device id so concierge can dedupe repeat visits.
    if (deviceUuid) {
      localStorage.setItem('uuid', deviceUuid);
    }
    this.client.recordDemoAccountView(deviceUuid, guestViewType)
      .catch(() => { /* tracking only — never block the demo flow */ });
  }

  /**
   * Converts the shared demo session into the user's own real account, in place,
   * without navigating or showing any UI. Used by the in-app "Get Started" flow so
   * account creation happens silently behind an open dialog. Leaves the demo
   * account untouched: it logs out of it (clearing the demo flag + storage) and
   * creates/authenticates the real account the user signed up with.
   * Deliberately does NOT publish 'auth:complete' (which would reload page data and
   * steal focus mid-flow) — only refreshes the menu via 'updateMenu'.
   */
  async convertDemoToRealAccount(formData: DemoPendingSignup, accountType: string): Promise<void> {
    this.dismissWelcome();
    await this.clearSessionStoragePreservingPosthog();
    await this.auth.login({}, 'signUp', null);
    const customersPayload = {
      customer: {
        booked_via: 'Self Booking',
        email: formData.email,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        password: formData.password,
        account_type: accountType
      }
    };
    const response: any = await this.client.getCustomers(customersPayload);
    localStorage.setItem('customer_id', response?.id);
    await this.auth.login(
      { username: formData.email, password: formData.password },
      'signUpPassword',
      null
    );
    this.auth.setSession();
    this.auth.setAccountTypeStorage(accountType);
    if (accountType === 'rental') {
      localStorage.setItem('isRentalClient', 'true');
      // New rental account → must schedule a setup call before booking. The book-job gate
      // (DemoBlockService.shouldGateNewRental) reads this flag; it's cleared once a call is
      // scheduled (markSetupCallScheduled) so existing rental users are never gated.
      localStorage.setItem('justCreatedAccount', 'true');
    }
    this.clearPendingSignup();
    localStorage.setItem('email', formData.email);
    localStorage.setItem('firstName', formData.firstName);
    localStorage.setItem('lastName', formData.lastName);
    await this.storage.save('isGuestAccount', false);
    await this.storage.delete('me');
    await this.me.load(true);
    this.auth.authenticated.set(true);
    // Identify the new real account in PostHog NOW, before GetStartedFlow.afterAccountCreated()
    // fires the conversion KPIs (created_account / scheduled_call / viewed_salesy_page). This
    // conversion deliberately skips the auth:complete reload — which is what would otherwise call
    // identify() via initPostHog — so without this the KPIs fire on the still-anonymous demo
    // distinct_id and never stitch to the real person: the exposure ($feature_flag_called, recorded
    // anonymously at demo login) and the conversion land on different PostHog people, so every demo
    // experiment fails to attribute the conversion. Identifying here merges the anonymous demo
    // session (carrying the exposure) into the real account. Best-effort — never block conversion.
    await this.posthogService
      .identify(Number(response?.id), formData.email, { account_type: accountType })
      .catch(() => undefined);
    // The created_account funnel KPI (shared by the pm-demo-landing + pm-create-account-phone
    // experiments) fires once from GetStartedFlow.afterAccountCreated(), not here, to avoid
    // double-counting across the email + social conversion paths.
    await this.markCreatedViaPmDemo();
    this.markConvertedFromDemo();
    this.events.publish('updateMenu');
  }

  /**
   * Social (Google/Apple) variant of {@link convertDemoToRealAccount}: the social
   * endpoint already returned a token for the real account, so we just adopt that
   * session in place — no reload, no navigation (the auth-modal path reloads for
   * guests, which would destroy the open "Get Started" dialog).
   */
  async convertDemoToRealAccountSocial(response: { access_token: string; token_type: string }): Promise<void> {
    this.dismissWelcome();
    await this.clearSessionStoragePreservingPosthog();
    localStorage.setItem('tidy_token', response.access_token);
    localStorage.setItem('tidy_token_type', response.token_type);
    this.clearPendingSignup();
    await this.storage.save('isGuestAccount', false);
    await this.storage.delete('me');
    const account = await this.me.load(true);
    this.auth.setAccountTypeStorage(account?.customer_account?.account_type);
    if (account?.customer_account?.account_type === 'rental') {
      localStorage.setItem('isRentalClient', 'true');
      // New rental account → gate booking until a setup call is scheduled (see
      // convertDemoToRealAccount above for the flag's lifecycle).
      localStorage.setItem('justCreatedAccount', 'true');
    }
    this.auth.authenticated.set(true);
    // Identify the new real account before the conversion KPIs fire in
    // GetStartedFlow.afterAccountCreated(), so the anonymous demo exposure stitches to the real
    // person and the experiment attributes the conversion — see convertDemoToRealAccount().
    // Best-effort — never block conversion.
    await this.posthogService
      .identify(
        Number(account?.customer_account?.id),
        account?.current_user?.email ?? '',
        { account_type: account?.customer_account?.account_type },
      )
      .catch(() => undefined);
    // created_account KPI fires once from GetStartedFlow.afterAccountCreated() (only for a
    // genuinely new account, is_new_user), not here — see convertDemoToRealAccount().
    await this.markCreatedViaPmDemo();
    this.markConvertedFromDemo();
    this.events.publish('updateMenu');
  }

  /**
   * Stamps the freshly-created real account with `onboarding.created_via = pm_demo`, the
   * concierge Funnel V2 membership + "Created Account" stage signal. Best-effort: a failed
   * write must never block the conversion. The key is passed WITHOUT the 'external.' prefix
   * (the TIDY API prepends it on write).
   */
  private async markCreatedViaPmDemo(): Promise<void> {
    await this.client.addMetadata({
      key: DemoAccountService.CREATED_VIA_META_KEY,
      value: DemoAccountService.CREATED_VIA_PM_DEMO,
    }).catch(() => undefined);
  }

  // Drop the in-memory route-reuse cache now (so no detached route can re-attach with
  // demo data) and arm a page reload for when the Get Started dialog closes.
  private markConvertedFromDemo(): void {
    CustomRouteReuseStrategy.clearStoredRoutes();
    this.pendingRealDataReload = true;
  }

  // True (once) if a demo→real conversion happened and the displayed page still needs to
  // be reloaded to drop its demo data. The app calls this when the dialog closes.
  consumePendingRealDataReload(): boolean {
    const pending = this.pendingRealDataReload;
    this.pendingRealDataReload = false;
    return pending;
  }

  /**
   * Signs the user into their existing real account from the demo session (the Get
   * Started flow's "Sign In" tab). Mirrors the auth modal's guest sign-in: log in, swap
   * the demo session for the real token, then full-reload so the app re-bootstraps into
   * the real account. Throws (leaving the demo session intact) on bad credentials.
   */
  async signInToRealAccount(credentials: { email: string; password: string }): Promise<void> {
    await this.auth.login({ username: credentials.email, password: credentials.password }, 'login', null);
    const token = localStorage.getItem('tidy_token');
    const tokenType = localStorage.getItem('tidy_token_type');
    this.dismissWelcome();
    await this.clearSessionStoragePreservingPosthog();
    localStorage.setItem('tidy_token', token!);
    localStorage.setItem('tidy_token_type', tokenType!);
    await this.storage.save('didJustSignIn', true);
    await this.storage.save('isGuestAccount', false);
    window.location.reload();
  }

  stashPendingSignup(formData: DemoPendingSignup): void {
    const { firstName, lastName, email, password } = formData;
    sessionStorage.setItem(
      DemoAccountService.PENDING_SIGNUP_KEY,
      JSON.stringify({ firstName, lastName, email, password })
    );
  }

  getPendingSignup(): DemoPendingSignup | null {
    try {
      const stored = sessionStorage.getItem(DemoAccountService.PENDING_SIGNUP_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  clearPendingSignup(): void {
    sessionStorage.removeItem(DemoAccountService.PENDING_SIGNUP_KEY);
  }

  // The entrance doubles as the loading state: beginEntrance() covers the screen
  // the moment the user picks the demo path (so login happens behind the curtain),
  // revealEntrance() wipes it away once the session is ready, the overview rises
  // in underneath, and the welcome card lands last.
  beginEntrance(): void {
    this.clearEntranceTimers();
    this.coverStartedAt = Date.now();
    this.entrancePhase.set('cover');
  }

  revealEntrance(): void {
    // Hold the cover long enough for the moment to land, even on a fast login
    const MIN_COVER_MS = 1500;
    const elapsed = Date.now() - this.coverStartedAt;
    const wait = Math.max(250, MIN_COVER_MS - elapsed);
    this.entranceTimers.push(setTimeout(() => {
      document.body.classList.add('demo-revealing');
      this.entrancePhase.set('reveal');
      this.entranceTimers.push(setTimeout(() => {
        this.entrancePhase.set(this.isDemoSession() ? 'welcome' : 'idle');
      }, 850));
      this.entranceTimers.push(setTimeout(() => {
        document.body.classList.remove('demo-revealing');
      }, 2600));
    }, wait));
  }

  // Bail out of the entrance (e.g. demo login failed) and uncover the screen
  cancelEntrance(): void {
    this.clearEntranceTimers();
    document.body.classList.remove('demo-revealing');
    this.entrancePhase.set('idle');
  }

  // If the app reloads before the welcome card was dismissed, show it again
  // (without replaying the sweep).
  resumeWelcomeIfPending(): void {
    if (this.isDemoSession() && localStorage.getItem(DemoAccountService.WELCOME_PENDING_KEY) === 'true') {
      this.entrancePhase.set('welcome');
    }
  }

  dismissWelcome(): void {
    localStorage.removeItem(DemoAccountService.WELCOME_PENDING_KEY);
    this.entrancePhase.set('idle');
    document.body.classList.remove('demo-revealing');
  }

  private clearEntranceTimers(): void {
    this.entranceTimers.forEach(timer => clearTimeout(timer));
    this.entranceTimers = [];
  }

  /**
   * Wipe the demo session from BOTH localStorage and Ionic storage while keeping the
   * PostHog keys (ph_*, posthog_variant_*), theme, and anonymous device 'uuid'.
   *
   * `this.storage.clear()` internally calls `localStorage.clear()` (preserving only the
   * theme keys), so it must be awaited BEFORE the keys are restored — otherwise it drops
   * the sticky experiment-variant cache. Losing that cache re-buckets the user and races
   * the synchronous `getVariant()` reads in the Get Started flow into the 'control'
   * default right after account creation (the cause of the schedule-call radios briefly
   * showing for an auto-learn user).
   */
  private async clearSessionStoragePreservingPosthog(): Promise<void> {
    const preservedKeys: [string, string][] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('ph_') || key?.startsWith('posthog_variant_') || key?.startsWith('tidy_theme') || key === 'uuid') {
        preservedKeys.push([key, localStorage.getItem(key)!]);
      }
    }
    await this.storage.clear();
    localStorage.clear();
    preservedKeys.forEach(([k, v]) => localStorage.setItem(k, v));
  }

  /**
   * One-line summary of the bucketed variant for all six PM-demo experiments, logged when a
   * visitor enters the demo (clicking "For Property Managers / Hosts" in the auth modal or
   * hitting /create-account/rental — both call loginToDemoAccount). Order matches the test
   * list: PM Demo Landing Page / PM Create Account Phone Number / PM Schedule Call Page /
   * PM Schedule Call Selection / PM Schedule Call First / PM Demo Tour, e.g.
   * "schedule / control / control / auto-learn / schedule-call-first / guided-tour". The tour
   * reads "n/a" on mobile, where it is deliberately never assigned. Resolves the variants
   * (no exposure — that's already recorded above); best-effort, never blocks.
   */
  private async logPmDemoVariants(): Promise<void> {
    try {
      const [landing, phone, scheduleCallPage, scheduleCallSelection, scheduleCallFirst, tour] = await Promise.all([
        this.pmDemoLanding.resolveLandingVariant(),
        this.pmCreateAccountPhone.resolveVariant(),
        this.pmAccountManager.resolveVariant(),
        this.pmScheduleCallSelection.resolveVariant(),
        this.pmScheduleCallFirst.resolveVariant(),
        this.pmDemoTour.resolveVariant(),
      ]);
      console.log(`${landing} / ${phone} / ${scheduleCallPage} / ${scheduleCallSelection} / ${scheduleCallFirst} / ${tour ?? 'n/a'}`);
    } catch {
      /* logging only — never block the demo flow */
    }
  }
}
